import React, { useEffect, useState } from 'react';
import { createPublicClient, http } from 'viem';
import { Activity, ExternalLink } from 'lucide-react';

/**
 * EpochCapacityPill
 *
 * Live viem read of the Base-mainnet WithdrawalGuard's per-epoch capacity.
 * Shows `epochLimit - epochWithdrawn` so any third party can see today's
 * available headroom for protocol-routed withdrawals.
 *
 * Honest scope (must stay in the UI):
 *   - This is a *protocol-wide* read, not user-specific.
 *   - It does NOT currently apply to KojiruStakingV2 unstakes \u2014 the Guard
 *     gates a lender-pool surface that is not yet user-active. The pill
 *     therefore renders an "applies to: future lender pool" caveat.
 *   - When the contract is unreachable, the pill renders an honest "unreachable"
 *     state rather than guessing.
 */

const MAINNET = {
  chainId: 8453,
  rpcUrl: 'https://mainnet.base.org',
  withdrawalGuard: '0xE3D4B3BA88f53C2D2a499cb46Cd4679dE6c2021E',
  explorer: 'https://basescan.org',
};

const GUARD_ABI = [
  { type: 'function', name: 'getStatus', stateMutability: 'view', inputs: [], outputs: [
      { name: '_enforcing', type: 'bool' },
      { name: '_epochStart', type: 'uint256' },
      { name: '_epochDuration', type: 'uint256' },
      { name: '_epochWithdrawn', type: 'uint256' },
      { name: '_epochLimit', type: 'uint256' },
      { name: '_tvlSnapshot', type: 'uint256' },
      { name: '_maxWithdrawBps', type: 'uint256' },
      { name: '_epochWithdrawalCount', type: 'uint256' },
      { name: '_totalLifetimeWithdrawn', type: 'uint256' },
      { name: '_totalEpochsElapsed', type: 'uint256' },
      { name: '_epochTimeRemaining', type: 'uint256' },
  ] },
];

const client = createPublicClient({
  chain: {
    id: MAINNET.chainId,
    name: 'Base',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: { default: { http: [MAINNET.rpcUrl] } },
  },
  transport: http(MAINNET.rpcUrl),
});

const fmtCents = (cents) => {
  if (cents == null) return '\u2014';
  const dollars = Number(cents) / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(2)}M`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(1)}K`;
  return `$${dollars.toFixed(2)}`;
};

export default function EpochCapacityPill({ variant = 'card' }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const s = await client.readContract({ address: MAINNET.withdrawalGuard, abi: GUARD_ABI, functionName: 'getStatus' });
        if (canceled) return;
        setState({
          enforcing: s[0],
          epochDuration: Number(s[2]),
          epochWithdrawn: BigInt(s[3]),
          epochLimit: BigInt(s[4]),
          maxWithdrawBps: Number(s[6]),
          epochTimeRemaining: Number(s[10]),
        });
      } catch (e) {
        if (!canceled) setError(e.shortMessage || e.message || String(e));
      }
    })();
    return () => { canceled = true; };
  }, []);

  if (error) {
    return (
      <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 px-4 py-2.5 text-xs text-rose-300" data-testid="epoch-capacity-pill-error">
        Could not read WithdrawalGuard \u2014 {error.slice(0, 200)}
      </div>
    );
  }
  if (!state) {
    return (
      <div className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-2.5 text-xs text-white/40 animate-pulse" data-testid="epoch-capacity-pill-loading">
        Reading WithdrawalGuard on Base mainnet\u2026
      </div>
    );
  }

  const remaining = state.epochLimit > state.epochWithdrawn ? state.epochLimit - state.epochWithdrawn : 0n;
  const pctUsed = state.epochLimit === 0n ? 0 : Number((state.epochWithdrawn * 10000n) / state.epochLimit) / 100;
  const hoursLeft = Math.max(0, Math.round(state.epochTimeRemaining / 3600));

  if (variant === 'inline') {
    return (
      <a
        href={`${MAINNET.explorer}/address/${MAINNET.withdrawalGuard}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/[0.06] border border-emerald-500/20 text-xs text-emerald-300 hover:bg-emerald-500/[0.10] transition"
        data-testid="epoch-capacity-pill"
      >
        <Activity className="w-3 h-3" />
        Protocol epoch capacity: <strong className="text-emerald-200">{fmtCents(remaining)} / {fmtCents(state.epochLimit)}</strong>
        <ExternalLink className="w-3 h-3 opacity-50" />
      </a>
    );
  }

  return (
    <div className="rounded-xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.04] to-transparent p-4" data-testid="epoch-capacity-card">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-400/80 font-semibold">Epoch capacity remaining</span>
        <a
          href={`${MAINNET.explorer}/address/${MAINNET.withdrawalGuard}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] text-white/50 hover:text-white/70 inline-flex items-center gap-1"
        >
          BaseScan <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="text-2xl font-bold text-emerald-300 tabular-nums">{fmtCents(remaining)}</span>
        <span className="text-xs text-white/40">of {fmtCents(state.epochLimit)} ({(100 - pctUsed).toFixed(1)}% left)</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden mb-2">
        <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all" style={{ width: `${Math.min(100, pctUsed)}%` }} />
      </div>
      <div className="flex items-center justify-between text-[11px] text-white/45">
        <span>Resets in ~{hoursLeft} h</span>
        <span>Cap: {state.maxWithdrawBps / 100}% / {Math.round(state.epochDuration / 3600)} h</span>
      </div>
      <p className="mt-2 text-[10px] text-white/35 leading-relaxed">
        Honest scope: this is the protocol-wide cap enforced by the on-chain Guard. It applies to the future lender pool, not to KojiruStakingV2 unstakes.
      </p>
    </div>
  );
}

export { EpochCapacityPill };
